import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { QUEBEC_SCHOOLS } from "../data/quebecSchools";
import { rememberWebReminders } from "../data/webReminders";
import { SMS_CONSENT_TEXT } from "../data/legalContent";

export const INVITE_KEY = "sp.invite";

const phoneKey = (value) => {
  const digits = (value || "").replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
};
const samePhone = (a, b) => Boolean(phoneKey(a)) && phoneKey(a) === phoneKey(b);

const schoolLabel = (school) => `${school.name} — ${school.city}`;

const formatPhone = (raw) => {
  const digits = (raw || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const emptyStudent = (overrides = {}) => ({
  id: `stu-${Math.random().toString(36).slice(2, 8)}`,
  gender: "garcon",
  name: "",
  email: "",
  grade: "",
  phone: "",
  school: "",
  precisions: "",
  ...overrides,
});

const initialForm = {
  parentName: "",
  email: "",
  phone: "",
  acceptTerms: false,
  smsReminders: false,
  students: [emptyStudent({ id: "stu-1" })],
};

const Progress = ({ step }) => (
  <div
    className="flex items-center justify-center gap-0 mb-8"
    aria-label={`Étape ${step} sur 3`}
  >
    {[1, 2, 3].map((n, i) => (
      <div key={n} className="flex items-center">
        <span
          className={`w-2.5 h-2.5 rounded-full transition-colors ${
            n <= step ? "bg-[#0B8F6A]" : "bg-gray-200"
          }`}
        />
        {i < 2 && (
          <span
            className={`w-12 sm:w-16 h-0.5 mx-1 transition-colors ${
              n < step ? "bg-[#0B8F6A]" : "bg-gray-200"
            }`}
          />
        )}
      </div>
    ))}
  </div>
);

const Field = ({ label, id, children }) => (
  <label className="block mb-4" htmlFor={id}>
    <span className="block text-sm font-semibold text-[#122118] mb-2">
      {label}
    </span>
    {children}
  </label>
);

const inputClass =
  "w-full rounded-xl border border-gray-200 bg-[#FBFCFC] px-4 py-3 text-[#122118] outline-none placeholder:italic placeholder:text-[#A9B8AF] focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20 transition";

function SchoolPicker({ id, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);

  useEffect(() => setQuery(value), [value]);

  const normalize = (text) =>
    text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const needle = normalize(query.trim());
  const matches =
    needle.length < 2
      ? []
      : QUEBEC_SCHOOLS.filter((school) =>
          normalize(`${school.name} ${school.city} ${school.network}`).includes(needle)
        ).slice(0, 8);

  return (
    <div className="relative">
      <input
        id={id}
        data-testid={id}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={`${id}-list`}
        className={inputClass}
        value={query}
        placeholder="Ex. : École Saint-Luc"
        onChange={(event) => {
          setQuery(event.target.value);
          onChange("");
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (
        <ul
          id={`${id}-list`}
          role="listbox"
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
        >
          {needle.length < 2 && (
            <li className="px-3 py-2 text-xs text-[#829C8D]">
              Tapez au moins 2 lettres. Écoles publiques et privées du Québec.
            </li>
          )}
          {needle.length >= 2 && matches.length === 0 && (
            <li className="px-3 py-2 text-xs text-[#829C8D]">Aucune école trouvée.</li>
          )}
          {matches.map((school) => {
            const label = schoolLabel(school);
            return (
              <li key={`${label}-${school.network}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={value === label}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(label);
                    setQuery(label);
                    setOpen(false);
                  }}
                  className="block w-full px-3 py-2 text-left hover:bg-[#E9FBF0]"
                >
                  <span className="block text-sm font-semibold text-[#122118]">{school.name}</span>
                  <span className="block text-[11px] text-[#4a5f53]">
                    {school.city} · {school.network}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const TrialOnboarding = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [gate, setGate] = useState({ status: "open", invite: "" });
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [confirmState, setConfirmState] = useState({ status: "idle", message: "", results: [] });
  const formRef = useRef(form);
  formRef.current = form;
  const confirmStarted = useRef(false);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setForm(initialForm);
    setError("");
    confirmStarted.current = false;
    setConfirmState({ status: "idle", message: "", results: [] });
    const invite = sessionStorage.getItem(INVITE_KEY) || "";
    setGate({ status: "open", invite: "" });
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/beta/status?invite=${encodeURIComponent(invite)}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.invite) {
          setForm((current) => ({ ...current, parentName: data.invite.name || "", email: data.invite.email || "" }));
          return setGate({ status: "invited", invite });
        }
        return setGate({ status: data.mode === "invite" ? "invite" : "open", invite: "" });
      })
      .catch(() => {});
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (step !== 4 || confirmStarted.current) return;
    confirmStarted.current = true;
    const current = formRef.current;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    setConfirmState({ status: "sending", message: "", results: [] });
    fetch(`${base}/api/signup/confirmations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        parent: {
          name: current.parentName,
          email: current.email,
          phone: current.phone,
        },
        students: current.students.map((student) => ({
          name: student.name,
          email: student.email,
          phone: student.phone,
          grade: student.grade,
          school: student.school,
          gender: student.gender,
          precisions: student.precisions || "",
        })),
        smsReminders: current.smsReminders,
        acceptTerms: current.acceptTerms,
        appUrl: window.location.origin,
        inviteToken: sessionStorage.getItem(INVITE_KEY) || "",
      }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const detail = typeof data.detail === "string" ? data.detail : "";
          setConfirmState({
            status: "error",
            message:
              response.status === 503
                ? "Les courriels de confirmation ne partent pas encore : Brevo n’est pas configuré."
                : detail || "L'envoi Brevo a échoué.",
            results: [],
          });
          return;
        }
        const failed = (data.results || []).filter((item) => item && item.ok === false);
        if (failed.length) {
          setConfirmState({
            status: "error",
            message: failed[0].error || "Brevo a refusé l'envoi.",
            results: data.results || [],
          });
          return;
        }
        if (data.waitlisted) {
          setConfirmState({ status: "waitlisted", message: "", results: [] });
          return;
        }
        sessionStorage.removeItem(INVITE_KEY);
        rememberWebReminders({
          reminderToken: data.reminderToken,
          smsReminders: Boolean(data.smsReminders),
        });
        setConfirmState({ status: "sent", message: "", results: data.results || [] });
      })
      .catch(() => {
        setConfirmState({
          status: "error",
          message: "Le serveur d'envoi ne répond pas.",
          results: [],
        });
      });
  }, [step]);

  if (!open) return null;

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const continueParent = () => {
    if (!form.parentName.trim() || !form.email.trim()) {
      setError("Remplis le prénom et le courriel.");
      return;
    }
    if (form.smsReminders && !form.phone.trim()) {
      setError("Pour recevoir des SMS, indique ton numéro. Sinon, décoche les SMS.");
      return;
    }
    if (!form.acceptTerms) {
      setError("Accepte la politique de confidentialité et les conditions d’utilisation pour continuer.");
      return;
    }
    setError("");
    setStep(2);
  };

  const patchStudent = (id, patch) =>
    setForm((current) => ({
      ...current,
      students: current.students.map((student) =>
        student.id === id ? { ...student, ...patch } : student
      ),
    }));

  const addStudent = () => {
    setError("");
    setForm((current) => ({
      ...current,
      students: [...current.students, emptyStudent({ gender: "fille" })],
    }));
  };

  const removeStudent = (id) => {
    setForm((current) => ({
      ...current,
      students: current.students.filter((student) => student.id !== id),
    }));
  };

  const continueStudent = () => {
    const missing = form.students.some(
      (student) => !student.name.trim() || !student.email.trim()
    );
    if (missing) {
      setError("Pour chaque élève, indique au moins le prénom et le courriel.");
      return;
    }
    if (form.students.some((student) => samePhone(student.phone, form.phone))) {
      setError("Le numéro de l’élève doit être différent de celui du parent.");
      return;
    }
    setError("");
    setStep(3);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      data-testid="trial-onboarding"
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-onboarding-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-[#122118]/45 backdrop-blur-[2px]"
        aria-label="Fermer"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.22 }}
        className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-3xl shadow-2xl border border-gray-100"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 w-9 h-9 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 transition"
          aria-label="Fermer l’inscription"
          data-testid="trial-onboarding-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 sm:p-8 pt-10">
          <AnimatePresence mode="wait">
            {step <= 3 && (
              <motion.div
                key={`progress-${step}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Progress step={step} />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step-1"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                {gate.status === "invite" && (
                  <p className="mb-2 text-xs font-semibold text-[#c0392b]" data-testid="trial-beta-notice">
                    Bêta privée sur invitation : chaque inscription est approuvée par notre équipe.
                  </p>
                )}
                <h2
                  id="trial-onboarding-title"
                  className="font-display text-2xl sm:text-3xl font-bold text-[#122118]"
                >
                  Créer votre compte famille
                </h2>
                <p className="mt-2 text-[#4a5f53] text-sm sm:text-base">
                  Nous avons besoin de votre prénom et de votre courriel. Le numéro sert seulement si vous acceptez les SMS.
                </p>

                <div className="mt-6">
                  <Field label="Prénom du parent" id="trial-parent">
                    <input
                      id="trial-parent"
                      data-testid="trial-parent-name"
                      className={inputClass}
                      value={form.parentName}
                      onChange={set("parentName")}
                      placeholder="Ex. : Sophie"
                      autoComplete="given-name"
                    />
                  </Field>
                  <Field label="Courriel du parent" id="trial-email">
                    <input
                      id="trial-email"
                      data-testid="trial-email"
                      type="email"
                      className={inputClass}
                      value={form.email}
                      onChange={set("email")}
                      readOnly={gate.status === "invited"}
                      placeholder="Ex. : sophie.martin@email.com"
                      autoComplete="email"
                    />
                    <span className="mt-1 block text-xs text-[#829C8D]">{gate.status === "invited" ? "Courriel de votre invitation." : "À confirmer par courriel."}</span>
                  </Field>
                  <Field label="Votre numéro (facultatif)" id="trial-phone">
                    <div className="flex gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-[#FBFCFC] px-3 text-sm font-medium text-[#122118]">
                        <span aria-hidden className="text-sm leading-none">🇨🇦</span>
                        <span>+1</span>
                      </span>
                      <input
                        id="trial-phone"
                        data-testid="trial-phone"
                        className={inputClass}
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="Ex. : (514) 555-0123"
                        type="tel"
                        autoComplete="tel"
                      />
                    </div>
                  </Field>
                </div>

                <label className="mb-4 flex items-start gap-3 text-sm text-[#4a5f53] leading-snug cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="trial-sms-reminders"
                    checked={form.smsReminders}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, smsReminders: e.target.checked }))
                    }
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-[#25D366] focus:ring-[#25D366]"
                  />
                  <span>{SMS_CONSENT_TEXT}</span>
                </label>

                {error && (
                  <p className="text-sm text-red-600 mb-3" role="alert">
                    {error}
                  </p>
                )}

                <label className="mb-4 flex items-start gap-3 text-sm text-[#4a5f53] leading-snug cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="trial-accept-terms"
                    checked={form.acceptTerms}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, acceptTerms: e.target.checked }))
                    }
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-[#25D366] focus:ring-[#25D366]"
                  />
                  <span>
                    J’accepte la{" "}
                    <a
                      href="/confidentialite"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-medium text-[#189346] hover:text-[#0B8F6A]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      politique de confidentialité
                    </a>{" "}
                    et les{" "}
                    <a
                      href="/conditions-utilisation"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-medium text-[#189346] hover:text-[#0B8F6A]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      conditions d’utilisation
                    </a>
                    .
                  </span>
                </label>

                <button
                  type="button"
                  data-testid="trial-parent-continue"
                  onClick={continueParent}
                  className="w-full rounded-full px-6 py-3.5 text-base font-semibold transition hover:opacity-95"
                  style={{
                    background: "var(--sp-primary)",
                    color: "var(--sp-text-on-primary)",
                  }}
                >
                  Continuer
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step-2"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#122118]">
                  Ajouter un élève
                </h2>
                <p className="mt-2 text-[#4a5f53] text-sm sm:text-base">
                  Chaque élève confirme aussi son courriel.
                </p>

                {form.students.map((student, index) => (
                  <div key={student.id} className={index ? "mt-8 border-t border-gray-100 pt-6" : "mt-6"}>
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-semibold text-[#122118]">
                        {form.students.length > 1 ? `Élève ${index + 1}` : "Élève"}
                      </p>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => removeStudent(student.id)}
                          className="text-xs font-semibold text-[#829C8D] underline"
                        >
                          Retirer
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Profil">
                      {[
                        { id: "fille", label: "Fille", emoji: "👧" },
                        { id: "garcon", label: "Garçon", emoji: "👦" },
                      ].map((g) => {
                        const selected = student.gender === g.id;
                        return (
                          <button
                            key={g.id}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            data-testid={`trial-gender-${student.id}-${g.id}`}
                            onClick={() => patchStudent(student.id, { gender: g.id })}
                            className={`rounded-2xl border-2 p-4 text-center transition ${
                              selected
                                ? "border-[#0B8F6A] bg-[#E9FBF0]"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                          >
                            <span className="text-3xl block mb-2" aria-hidden>
                              {g.emoji}
                            </span>
                            <span className="font-semibold text-[#122118]">{g.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-5">
                      <Field label="Prénom de l'élève" id={`trial-student-name-${student.id}`}>
                        <input
                          id={`trial-student-name-${student.id}`}
                          data-testid={`trial-student-name-${student.id}`}
                          className={inputClass}
                          value={student.name}
                          onChange={(event) => patchStudent(student.id, { name: event.target.value })}
                          placeholder="Ex. : Émile"
                        />
                      </Field>
                      <Field label="Courriel de l'élève" id={`trial-student-email-${student.id}`}>
                        <input
                          id={`trial-student-email-${student.id}`}
                          data-testid={`trial-student-email-${student.id}`}
                          type="email"
                          className={inputClass}
                          value={student.email}
                          onChange={(event) => patchStudent(student.id, { email: event.target.value })}
                          placeholder="Ex. : emile.martin@email.com"
                          autoComplete="email"
                        />
                        <span className="mt-1 block text-xs text-[#829C8D]">À confirmer par courriel.</span>
                      </Field>
                      <Field label="École (facultatif)" id={`trial-student-school-${student.id}`}>
                        <SchoolPicker
                          id={`trial-student-school-${student.id}`}
                          value={student.school}
                          onChange={(school) => patchStudent(student.id, { school })}
                        />
                      </Field>
                      <Field label="Niveau scolaire (facultatif)" id={`trial-grade-${student.id}`}>
                        <select
                          id={`trial-grade-${student.id}`}
                          data-testid={`trial-grade-${student.id}`}
                          className={`${inputClass} [&>option]:not-italic [&>option]:text-[#122118] ${student.grade ? "" : "italic text-[#A9B8AF]"}`}
                          value={student.grade}
                          onChange={(event) => patchStudent(student.id, { grade: event.target.value })}
                        >
                          <option value="" disabled>
                            Choisir le niveau
                          </option>
                          <option>Secondaire 1</option>
                          <option>Secondaire 2</option>
                          <option>Secondaire 3</option>
                          <option>Secondaire 4</option>
                          <option>Secondaire 5</option>
                        </select>
                      </Field>
                      <Field label="Son numéro (facultatif)" id={`trial-student-phone-${student.id}`}>
                        <div className="flex gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-[#FBFCFC] px-3 text-sm font-medium text-[#122118]">
                            <span aria-hidden className="text-sm leading-none">🇨🇦</span>
                            <span>+1</span>
                          </span>
                          <input
                            id={`trial-student-phone-${student.id}`}
                            data-testid={`trial-student-phone-${student.id}`}
                            className={inputClass}
                            value={student.phone}
                            onChange={(event) => patchStudent(student.id, { phone: event.target.value })}
                            placeholder="Ex. : (514) 555-0199"
                            type="tel"
                            autoComplete="tel"
                          />
                        </div>
                      </Field>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  data-testid="trial-add-student"
                  onClick={addStudent}
                  className="mt-2 mb-3 text-sm font-semibold text-[#189346] underline"
                >
                  Ajouter un autre élève
                </button>
                <button
                  type="button"
                  data-testid="trial-invite-later"
                  onClick={() => {
                    setError("");
                    setForm((current) => ({ ...current, students: [] }));
                    setStep(4);
                  }}
                  className="mb-6 block text-sm font-semibold text-[#4a5f53] underline"
                >
                  Inviter mon enfant plus tard, depuis l’application
                </button>

                {error && (
                  <p className="text-sm text-red-600 mb-3" role="alert">
                    {error}
                  </p>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setError("");
                      setStep(1);
                    }}
                    className="rounded-full px-5 py-3.5 text-sm font-semibold text-[#4a5f53] border border-gray-200 hover:bg-gray-50"
                  >
                    Retour
                  </button>
                  <button
                    type="button"
                    data-testid="trial-student-continue"
                    onClick={continueStudent}
                    className="flex-1 rounded-full px-6 py-3.5 text-base font-semibold transition hover:opacity-95"
                    style={{
                      background: "var(--sp-primary)",
                      color: "var(--sp-text-on-primary)",
                    }}
                  >
                    Continuer
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step-3"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#122118]">
                  Quelques précisions d’abord !
                </h2>
                <p className="mt-2 text-[#4a5f53] text-sm sm:text-base">
                  Cela nous aidera à personnaliser les plans d’étude.
                </p>

                <div className="mt-6 rounded-2xl overflow-hidden border border-gray-100 bg-[#F7FAF8]">
                  <div
                    className="h-36 bg-cover bg-center"
                    style={{
                      backgroundImage:
                        "linear-gradient(120deg, rgba(37,211,102,.18), rgba(18,33,24,.08)), url(https://images.pexels.com/photos/4145190/pexels-photo-4145190.jpeg?auto=compress&cs=tinysrgb&w=800)",
                    }}
                    role="img"
                    aria-label="Élève qui étudie"
                  />
                </div>

                {form.students.map((student) => {
                  const name = student.name.trim() || "votre enfant";
                  const fieldId = `trial-precisions-${student.id}`;
                  return (
                    <label key={student.id} className="block mt-4" htmlFor={fieldId}>
                      <span className="block text-sm font-semibold text-[#122118] mb-2">
                        {form.students.length > 1 ? `Précisions pour ${name}` : "Précisions"}{" "}
                        <span className="font-normal text-[#829C8D]">(optionnel)</span>
                      </span>
                      <textarea
                        id={fieldId}
                        data-testid={fieldId}
                        rows={form.students.length > 1 ? 3 : 4}
                        className={`${inputClass} resize-none`}
                        placeholder={`Quels sont les principaux défis ${/^[aeiouyhàâéèêëîïôöùûü]/i.test(name) ? "d'" : "de "}${name} avec ses études et les difficultés que cela vous occasionne comme parent ?`}
                        value={student.precisions || ""}
                        onChange={(event) =>
                          patchStudent(student.id, { precisions: event.target.value })
                        }
                      />
                    </label>
                  );
                })}

                <p className="mt-3 text-xs text-[#829C8D]">
                  Vous pourrez modifier les précisions dans vos réglages. L’étape suivante envoie les courriels de confirmation.
                </p>

                <div className="mt-5 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="rounded-full px-5 py-3.5 text-sm font-semibold text-[#4a5f53] border border-gray-200 hover:bg-gray-50"
                  >
                    Retour
                  </button>
                  <button
                    type="button"
                    data-testid="trial-questionnaire-start"
                    onClick={() => setStep(4)}
                    className="flex-1 rounded-full px-6 py-3.5 text-base font-semibold transition hover:opacity-95"
                    style={{
                      background: "var(--sp-primary)",
                      color: "var(--sp-text-on-primary)",
                    }}
                  >
                    Continuer
                  </button>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step-4"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="text-center py-2"
              >
                <div
                  className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-5"
                  style={{ background: "#E9FBF0" }}
                >
                  <Check className="w-8 h-8 text-[#189346]" strokeWidth={3} />
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#122118]" data-testid="trial-done-title">
                  {confirmState.status === "waitlisted" ? "Demande reçue" : "Confirmez par courriel"}
                </h2>
                <p className="mt-2 text-[#4a5f53] leading-relaxed max-w-sm mx-auto">
                  {confirmState.status === "waitlisted"
                    ? "Plan Étude est en bêta privée sur invitation. Notre équipe approuve votre inscription sous peu."
                    : "Ouvrez le lien reçu par courriel pour activer le compte."}
                </p>
                {confirmState.results?.[0]?.confirmPath && (
                  <p className="mt-3 text-sm text-[#122118]">
                    Mode local :{" "}
                    <a
                      className="font-semibold text-[#128a3e] underline break-all"
                      href={confirmState.results[0].confirmPath}
                      data-testid="trial-local-confirm"
                    >
                      ouvrir la confirmation
                    </a>
                  </p>
                )}
                <div className="mt-5 text-left text-sm text-[#122118] max-w-sm mx-auto space-y-8">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#189346]">{confirmState.status === "waitlisted" ? "Votre demande" : "Dès maintenant"}</p>
                    <ul className="mt-1 space-y-1">
                      <li>
                        <span className="font-semibold">{form.parentName || "Parent"}</span>
                        {" · "}
                        {form.email}
                        {form.phone.trim() ? ` · ${formatPhone(form.phone)}` : ""}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#189346]">
                      {confirmState.status === "waitlisted" ? "Après approbation" : "Après votre confirmation"}
                    </p>
                    <ul className="mt-1 space-y-1">
                      {form.students.map((student) => (
                        <li key={student.id}>
                          <span className="font-semibold">{student.name || "Élève"}</span>
                          {" · "}
                          {student.email}
                          {student.phone.trim() ? ` · ${formatPhone(student.phone)}` : ""}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-xs text-[#829C8D]">
                      {form.students.length
                        ? "Chaque élève reçoit son invitation par courriel."
                        : "Vous inviterez votre enfant depuis l’application."}
                    </p>
                  </div>
                </div>
                <p className="mt-4 text-sm text-[#829C8D] leading-relaxed max-w-sm mx-auto">
                  {confirmState.status === "sending" && "Envoi du courriel…"}
                  {confirmState.status === "sent" &&
                    (confirmState.results?.[0]?.delivered === false
                      ? "Mode local : aucun courriel envoyé. Utilisez le lien ci-dessus."
                      : "Le lien est dans la boîte de réception.")}
                  {confirmState.status === "error" && confirmState.message}
                  {confirmState.status === "waitlisted" && "Vous recevrez le courriel de confirmation dès que votre accès est approuvé."}
                  {confirmState.status === "idle" && "Le courriel part à cette étape."}
                </p>

                <button
                  type="button"
                  data-testid="trial-back-home"
                  onClick={onClose}
                  className="mt-7 w-full rounded-full px-6 py-3.5 text-base font-semibold transition hover:opacity-95"
                  style={{
                    background: "var(--sp-primary)",
                    color: "var(--sp-text-on-primary)",
                  }}
                >
                  Retour à l’accueil
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default TrialOnboarding;
